/**
 * Format a number as GBP currency.
 * Only shows decimal places when there are actual pennies.
 *   £853     (not £853.00)
 *   £853.55  (keeps pennies)
 */
export const fmtCurrency = (n) => {
  if (n == null) return null;
  const num = parseFloat(n);
  if (isNaN(num)) return null;
  const hasPennies = num % 1 !== 0;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: hasPennies ? 2 : 0,
    maximumFractionDigits: hasPennies ? 2 : 0,
  }).format(num);
};

/**
 * Format a single rate value with its unit.
 * Handles percentages specially (e.g. "50%" or "50.5%").
 */
export const fmtRate = (n, unit) => {
  if (n == null) return null;
  if (unit === '% of costs') {
    const num = parseFloat(n);
    return `${num % 1 === 0 ? Math.round(num) : num}%`;
  }
  return fmtCurrency(n);
};

/**
 * Format a rate value with its unit string.
 * Ensures a space before units that don't start with '/'.
 *   £732 / ha / year    (slash-prefixed units get spaces around /)
 *   £500 lump sum       (non-slash units get a space before)
 *   50% of costs        (percentage handled separately)
 */
const fmtUnit = (unit) => {
  if (!unit || unit === '% of costs') return '';
  // Units starting with '/' attach directly (£732/ha), others get a space (£732 lump sum)
  return unit.startsWith('/') ? unit : ` ${unit}`;
};

/**
 * Format a single payment rate object into a display string.
 * Returns null if no rate values.
 *   "£732 / ha / year"
 *   "£500 – £1,000 / agreement"
 *   "50% of costs"
 */
export const formatSingleRate = (rate) => {
  const min = parseFloat(rate.rate_minimum);
  const max = parseFloat(rate.rate_maximum);
  if (!min && !max) return null;

  const isPercent = rate.unit === '% of costs';
  const amount = (min === max || !max)
    ? fmtRate(min || max, rate.unit)
    : `${fmtRate(min, rate.unit)} – ${fmtRate(max, rate.unit)}`;

  if (isPercent) return `${amount} of costs`;
  return `${amount}${fmtUnit(rate.unit)}`;
};

/**
 * Format all payment rates for a funding scheme into a single string.
 * Used by FundingRow, FundingCard, CompatibilityModem, etc.
 *   "£732 / ha / year; £500 lump sum"
 */
export const formatPaymentRates = (paymentRates, { maxRates } = {}) => {
  if (!paymentRates || paymentRates.length === 0) return 'Not specified';
  const parts = paymentRates.map(formatSingleRate).filter(Boolean);
  if (parts.length === 0) return 'Not specified';
  if (maxRates && parts.length > maxRates) {
    return parts.slice(0, maxRates).join('; ') + ' …';
  }
  return parts.join('; ');
};

/**
 * Get structured earning data for detail view display.
 * Returns array of { value, unit } objects for rich rendering.
 */
export const getAllEarnings = (paymentRates) => {
  if (!paymentRates || paymentRates.length === 0) return [];
  return paymentRates
    .map(r => {
      const min = r.rate_minimum;
      const max = r.rate_maximum;
      if (!min && !max) return null;
      const isPercent = r.unit === '% of costs';
      const value = (min === max || !max)
        ? fmtRate(min || max, r.unit)
        : `${fmtRate(min, r.unit)} – ${fmtRate(max, r.unit)}`;
      return { value, unit: isPercent ? null : r.unit };
    })
    .filter(Boolean);
};

// Keep legacy export name for anything that imports it
export const formatCurrency = fmtCurrency;

// ── Date & time formatting ───────────────────────────────────────

/**
 * Safely format a date string to "15 January 2024".
 * Returns null for empty/null values.
 * Returns the original string unchanged if it can't be parsed as a date.
 */
export const formatDate = (dateString) => {
  if (!dateString) return null;
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return String(dateString);
  }
};

/**
 * Safely format a date string to "15 January 2024, 10:30".
 * Returns null for empty/null values.
 * Returns the original string unchanged if it can't be parsed as a date.
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return null;
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);
    return d.toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(dateString);
  }
};

// ── Funding scheme date helpers ──────────────────────────────────

/** Deadline tags that are self-describing display values (no date needed). */
const DESCRIPTIVE_DEADLINE_TAGS = new Set(['Rolling', 'Annual', 'No Deadline']);

/** Values from the API that should be treated as empty / not provided. */
const UNKNOWN_VALUES = new Set(['unknown', 'Unknown']);

/**
 * Get display text for a funding scheme's deadline.
 *
 * - "Rolling" / "Annual" / "No Deadline" tags → tag is the display value
 * - "Fixed" / "Not Specified" / "Other" / empty → show formatted deadline_date
 * - "Unknown" / "unknown" from the API → treated as missing, returns fallback
 * - Falls back to `fallback` if nothing useful is available
 */
export const getDeadlineDisplay = (funding, fallback = 'Not specified') => {
  const tag = funding.deadline_tag?.trim();

  if (tag && !UNKNOWN_VALUES.has(tag) && DESCRIPTIVE_DEADLINE_TAGS.has(tag)) return tag;

  const date = funding.deadline_date?.trim();
  if (date && !UNKNOWN_VALUES.has(date)) return formatDate(date) ?? fallback;

  return fallback;
};

/**
 * Format free-text display dates like "1 Aug 2025", but leave text without a
 * day (e.g. "March 2024", "Spring 2025") as-is so we don't invent a day.
 */
const formatDisplayDate = (text) => (/\b\d{1,2}\b/.test(text) ? formatDate(text) : text);

/**
 * Get display text for when a scheme opens.
 * Prefers the actual DateField (formatted consistently), falls back to opens_display.
 */
export const getOpensDisplay = (funding, fallback = null) => {
  if (funding.opens) return formatDate(funding.opens);
  if (funding.opens_display) return formatDisplayDate(funding.opens_display);
  return fallback;
};

/**
 * Get display text for when a scheme closes.
 * Prefers the actual DateField (formatted consistently), falls back to closes_display.
 */
export const getClosesDisplay = (funding, fallback = null) => {
  if (funding.closes) return formatDate(funding.closes);
  if (funding.closes_display) return formatDisplayDate(funding.closes_display);
  return fallback;
};

export const truncateText = (text, maxLength = 150) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const getStatusColor = (status) => {
  if (!status) return 'bg-gray-100 text-gray-800';

  const normalized = status.toLowerCase();
  const colors = {
    open: 'bg-green-100 text-green-800',
    closed: 'bg-red-100 text-red-800',
    coming_soon: 'text-white',
    invitation_only: 'text-white',
    historic: 'bg-gray-200 text-gray-600',
  };

  return colors[normalized] || 'bg-gray-100 text-gray-800';
};

export const calculateDaysUntilDeadline = (deadline) => {
  if (!deadline) return null;
  const today = new Date();
  const deadlineDate = new Date(deadline);
  const diffTime = deadlineDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const buildQueryString = (filters) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value);
    }
  });

  return params.toString();
};
